import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { QUERY_ADVANCED_FILTERS } from '@/graphql/queries/filters';
import { QUERY_GEOMETRY } from '@/graphql/queries/geometry';
import { AdvancedFilter } from '@/graphql/types/filter';
import { useApolloClient } from '@apollo/client';
import { Classification } from '@/types/classification';
import { useLocation } from 'react-router-dom';
import { AppRouters } from '@/utils/constants/routers';
import { OpportunityAndRisk } from '@/types/opportunityAndRisk';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/hooks/useLanguage';
import { useAdvancedFiltersUrl } from '@/hooks/useAdvancedFiltersUrl';
import {
  firstDynamicFilterCode,
  removeMunicipalityCodeFromUrl,
} from '@/utils/functions/advancedFilters';
import { Divider, useMediaQuery } from '@mui/material';
import {
  Buttons,
  // ClassificationButton,
  // ClassificationButtonGroup,
  ScoreFilterContainer,
  SearchMunicipalityFormFooter,
  StyledSearchContainer,
} from './styles';
import { Button, LoadingButton } from '@/components/atoms/Button';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { Flex } from '@/styles/globalStyles';
import { Text } from '@/components/atoms/text';
import { ScoreFilter } from '@/components/molecules/score/filter';
import { Field } from '@/components/atoms/field';
import { Label } from '@/components/atoms/label';
import { AutocompleteStyled } from '@/components/atoms/inputs/autocomplete';
import { useTheme } from '@mui/material/styles';
import { useGeometry } from '@/hooks/useGeometry';
import { Accordion } from '@/components/atoms/accordion';
import { SearchMunicipalityFormDimensions, DimensionLevelFilter } from './dimensions';
import { Add, Remove } from '@mui/icons-material';
import { Desktop } from '@/components/molecules/viewport/desktop';
import { DimensionTag } from '@/components/molecules/dimensionTag';

interface SearchFormProps {
  onCloseBottomSheet?: () => void;
  toggleAdvancedSearch?: () => void;
}

const opportunityAndRiskOptions = [
  OpportunityAndRisk.ALL,
  OpportunityAndRisk.HIGH,
  OpportunityAndRisk.MEDIUM_HIGH,
  OpportunityAndRisk.MEDIUM_LOW,
  OpportunityAndRisk.LOW,
];

export const SearchMunicipalityForm = ({
  onCloseBottomSheet,
  toggleAdvancedSearch,
}: SearchFormProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const apolloClient = useApolloClient();
  const { pathname } = useLocation();

  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { setFiltersToUrl, getFiltersFromUrl } = useAdvancedFiltersUrl();
  const {
    getGeometry,
    geometryTotal,
    setGeometryTotal,
    loading: loadingGeometry,
    searchFilter,
    clearSavedMapData,
    setAdvancedSearchTotalResults,
    resetFitBounds,
    setStartSearch,
    setIsMunicipality,
  } = useGeometry();

  const filtersFromUrl = getFiltersFromUrl();

  const [region, setRegion] = useState<string>();
  const [biome, setBiome] = useState<string>();
  const [state, setState] = useState<string>();
  const [classification, setClassification] = useState<Classification>();

  const [filtersData, setFiltersData] = useState<AdvancedFilter['filters']>();
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [opportunityLevels, setOpportunityLevels] = useState<OpportunityAndRisk[]>(() => {
    const globalFilter = filtersFromUrl?.dimensionLevelFilters?.find(
      (filter) => filter.dimension_id === null
    );

    if (globalFilter?.level_opportunity?.length) {
      return globalFilter.level_opportunity as OpportunityAndRisk[];
    }

    return [OpportunityAndRisk.ALL];
  });
  const [riskLevels, setRiskLevels] = useState<OpportunityAndRisk[]>(() => {
    const globalFilter = filtersFromUrl?.dimensionLevelFilters?.find(
      (filter) => filter.dimension_id === null
    );

    if (globalFilter?.level_risk?.length) {
      return globalFilter.level_risk as OpportunityAndRisk[];
    }

    return [OpportunityAndRisk.ALL];
  });

  const [isTerritorialScopeOpen, setIsTerritorialScopeOpen] = useState(true);

  const [intermediateGeographicRegion, setIntermediateGeographicRegion] = useState<
    string | undefined
  >(() => firstDynamicFilterCode(filtersFromUrl.dynamicFilters, 'intermediate_region'));
  const [immediateGeographicRegion, setImmediateGeographicRegion] = useState<string | undefined>(
    () => firstDynamicFilterCode(filtersFromUrl.dynamicFilters, 'immediate_region')
  );

  const [dimensionLevelFilters, setDimensionLevelFilters] = useState<DimensionLevelFilter[]>(() => {
    // Inicializar dimensionLevelFilters da URL
    return filtersFromUrl?.dimensionLevelFilters || [];
  });

  const globalOpportunityCount = useMemo(
    () => opportunityLevels.filter((level) => level !== OpportunityAndRisk.ALL).length,
    [opportunityLevels]
  );
  const globalRiskCount = useMemo(
    () => riskLevels.filter((level) => level !== OpportunityAndRisk.ALL).length,
    [riskLevels]
  );

  const regionOptions = filtersData?.region || [];
  const biomeOptions = filtersData?.biome || [];
  const stateOptions = filtersData?.state || [];
  const intermediateRegionOptions = filtersData?.intermediate_region;
  const immediateRegionOptions = filtersData?.immediate_region;

  const regionOptionsMapped = regionOptions.map((data) => ({
    label: data.name,
    value: data.value,
    disabled: !data.available,
  }));

  const biomeOptionsMapped = biomeOptions.map((data) => ({
    label: data.name,
    value: data.value,
    disabled: !data.available,
  }));

  const stateOptionsMapped = stateOptions.map((data) => ({
    label: data.name,
    value: data.value,
    disabled: !data.available,
  }));

  const intermediateGeographicRegionOptionsMapped = useMemo(() => {
    const list = intermediateRegionOptions ?? [];
    return list.map((data) => ({
      label: data.name,
      value: data.value,
      disabled: !data.available,
    }));
  }, [intermediateRegionOptions]);

  const immediateGeographicRegionOptionsMapped = useMemo(() => {
    const list = immediateRegionOptions ?? [];
    return list.map((data) => ({
      label: data.name,
      value: data.value,
      disabled: !data.available,
    }));
  }, [immediateRegionOptions]);

  const classificationOptionsMapped = [
    {
      label: t('label.label.all'),
      value: undefined,
    },
    {
      label: t('label.label.rural'),
      value: Classification.RURAL,
    },
    {
      label: t('label.label.urban'),
      value: Classification.URBAN,
    },
  ];

  const prevFilters = useRef({
    region,
    biome,
    state,
    intermediateGeographicRegion,
    immediateGeographicRegion,
    classification,
    opportunityLevels,
    riskLevels,
    dimensionLevelFilters,
  });

  const hasInitialFiltersLoaded = useRef(false);

  const createDynamicFilters = useCallback(() => {
    const dynamicFilter: Array<{
      field: string;
      operator: string;
      value: string | number | string[] | number[];
    }> = [];

    if (region) {
      dynamicFilter.push({
        field: 'region',
        operator: 'eq',
        value: region,
      });
    }

    if (state) {
      dynamicFilter.push({
        field: 'state',
        operator: 'eq',
        value: state,
      });
    }

    if (biome) {
      dynamicFilter.push({
        field: 'biome',
        operator: 'eq',
        value: biome,
      });
    }

    if (classification) {
      dynamicFilter.push({
        field: 'classification',
        operator: 'eq',
        value: classification,
      });
    }

    if (intermediateGeographicRegion) {
      dynamicFilter.push({
        field: 'intermediate_region',
        operator: 'in',
        value: [intermediateGeographicRegion],
      });
    }

    if (immediateGeographicRegion) {
      dynamicFilter.push({
        field: 'immediate_region',
        operator: 'in',
        value: [immediateGeographicRegion],
      });
    }

    return dynamicFilter;
  }, [
    region,
    state,
    biome,
    classification,
    intermediateGeographicRegion,
    immediateGeographicRegion,
  ]);

  const searchResults = useMemo(() => {
    return `${t('button.button.search')} (${geometryTotal} ${t('button.button.search.results')})`;
  }, [geometryTotal, t]);

  const handleOpportunityLevelsChange = (levels: OpportunityAndRisk[]) => {
    setOpportunityLevels(levels);
  };

  const handleRiskLevelsChange = (levels: OpportunityAndRisk[]) => {
    setRiskLevels(levels);
  };

  const clearFilters = useCallback(() => {
    setRegion(undefined);
    setBiome(undefined);
    setState(undefined);
    setIntermediateGeographicRegion(undefined);
    setImmediateGeographicRegion(undefined);
    setClassification(undefined);
    setOpportunityLevels([OpportunityAndRisk.ALL]);
    setRiskLevels([OpportunityAndRisk.ALL]);
    setDimensionLevelFilters([]);
    setStartSearch(false);
    setFiltersToUrl(AppRouters.home, {
      dynamicFilters: [],
      dimensionLevelFilters: [],
    });
  }, [setStartSearch, setFiltersToUrl]);

  const handleAdvancedFormSubmit = useCallback(async () => {
    setIsMunicipality(false);
    removeMunicipalityCodeFromUrl();

    setStartSearch(true);
    toggleAdvancedSearch?.();
    clearSavedMapData();

    setAdvancedSearchTotalResults(geometryTotal || 0);

    const currentFilters = getFiltersFromUrl();
    const dimensionIdFilter = currentFilters.dynamicFilters?.find(
      (f) => f.field === 'dimension_id'
    );
    const additionalMunicipalityCodes = currentFilters.additionalMunicipalityCodes || [];

    const dynamicFilters = createDynamicFilters();

    if (dimensionIdFilter) {
      dynamicFilters.push(dimensionIdFilter);
    }

    // Adicionar opportunityLevels e riskLevels ao dimensionLevelFilters
    const updatedDimensionLevelFilters = [...dimensionLevelFilters];

    // Verificar se há filtros globais de oportunidade/risco que não sejam ALL
    const hasGlobalOpportunityFilter =
      opportunityLevels.length > 0 && !opportunityLevels.includes(OpportunityAndRisk.ALL);
    const hasGlobalRiskFilter =
      riskLevels.length > 0 && !riskLevels.includes(OpportunityAndRisk.ALL);

    if (hasGlobalOpportunityFilter || hasGlobalRiskFilter) {
      // Procurar se já existe um filtro com dimension_id: null
      const existingGlobalFilterIndex = updatedDimensionLevelFilters.findIndex(
        (filter) => filter.dimension_id === null
      );

      const globalFilter: DimensionLevelFilter = {
        dimension_id: null,
        level_opportunity: hasGlobalOpportunityFilter
          ? opportunityLevels.filter((level) => level !== OpportunityAndRisk.ALL)
          : [],
        level_risk: hasGlobalRiskFilter
          ? riskLevels.filter((level) => level !== OpportunityAndRisk.ALL)
          : [],
      };

      if (existingGlobalFilterIndex >= 0) {
        // Atualizar filtro existente
        updatedDimensionLevelFilters[existingGlobalFilterIndex] = globalFilter;
      } else {
        // Adicionar novo filtro global
        updatedDimensionLevelFilters.unshift(globalFilter);
      }
    } else {
      // Remover filtro global se existir
      const existingGlobalFilterIndex = updatedDimensionLevelFilters.findIndex(
        (filter) => filter.dimension_id === null
      );
      if (existingGlobalFilterIndex >= 0) {
        updatedDimensionLevelFilters.splice(existingGlobalFilterIndex, 1);
      }
    }

    // Remove municipality_code from URL after setting filters
    removeMunicipalityCodeFromUrl();

    setStartSearch(true);
    setFiltersToUrl(AppRouters.home, {
      dynamicFilters,
      ...(additionalMunicipalityCodes.length > 0 && { additionalMunicipalityCodes }),
      ...(updatedDimensionLevelFilters.length > 0 && {
        dimensionLevelFilters: updatedDimensionLevelFilters,
      }),
    });

    resetFitBounds();

    await getGeometry({
      filters: {
        dynamicFilters,
        ...(additionalMunicipalityCodes.length > 0 && { additionalMunicipalityCodes }),
        ...(updatedDimensionLevelFilters.length > 0 && {
          dimensionLevelFilters: updatedDimensionLevelFilters,
        }),
      },
      pagination: { limit: 5600 },
    });

    onCloseBottomSheet?.();
  }, [
    region,
    biome,
    state,
    intermediateGeographicRegion,
    immediateGeographicRegion,
    classification,
    opportunityLevels,
    riskLevels,
    dimensionLevelFilters,
    createDynamicFilters,
    toggleAdvancedSearch,
    setIsMunicipality,
    clearSavedMapData,
    setAdvancedSearchTotalResults,
    setFiltersToUrl,
    geometryTotal,
    setStartSearch,
    getGeometry,
    resetFitBounds,
    onCloseBottomSheet,
    getFiltersFromUrl,
  ]);

  const getFilters = useCallback(async () => {
    const dynamicFilters = createDynamicFilters();
    const currentFilters = getFiltersFromUrl();
    const additionalMunicipalityCodes = currentFilters.additionalMunicipalityCodes || [];

    // Adicionar opportunityLevels e riskLevels ao dimensionLevelFilters
    const updatedDimensionLevelFilters = [...dimensionLevelFilters];

    // Verificar se há filtros globais de oportunidade/risco que não sejam ALL
    const hasGlobalOpportunityFilter =
      opportunityLevels.length > 0 && !opportunityLevels.includes(OpportunityAndRisk.ALL);
    const hasGlobalRiskFilter =
      riskLevels.length > 0 && !riskLevels.includes(OpportunityAndRisk.ALL);

    if (hasGlobalOpportunityFilter || hasGlobalRiskFilter) {
      // Procurar se já existe um filtro com dimension_id: null
      const existingGlobalFilterIndex = updatedDimensionLevelFilters.findIndex(
        (filter) => filter.dimension_id === null
      );

      const globalFilter: DimensionLevelFilter = {
        dimension_id: null,
        level_opportunity: hasGlobalOpportunityFilter
          ? opportunityLevels.filter((level) => level !== OpportunityAndRisk.ALL)
          : [],
        level_risk: hasGlobalRiskFilter
          ? riskLevels.filter((level) => level !== OpportunityAndRisk.ALL)
          : [],
      };

      if (existingGlobalFilterIndex >= 0) {
        // Atualizar filtro existente
        updatedDimensionLevelFilters[existingGlobalFilterIndex] = globalFilter;
      } else {
        // Adicionar novo filtro global
        updatedDimensionLevelFilters.unshift(globalFilter);
      }
    } else {
      // Remover filtro global se existir
      const existingGlobalFilterIndex = updatedDimensionLevelFilters.findIndex(
        (filter) => filter.dimension_id === null
      );
      if (existingGlobalFilterIndex >= 0) {
        updatedDimensionLevelFilters.splice(existingGlobalFilterIndex, 1);
      }
    }

    const filters = {
      dynamicFilters,
      ...(additionalMunicipalityCodes.length > 0 && { additionalMunicipalityCodes }),
      ...(updatedDimensionLevelFilters.length > 0 && {
        dimensionLevelFilters: updatedDimensionLevelFilters,
      }),
    };

    const { data } = await apolloClient.query<{
      advancedFilterOptions: AdvancedFilter;
    }>({
      query: QUERY_ADVANCED_FILTERS,
      variables: {
        filters,
        language: currentLanguage,
      },
    });

    if (additionalMunicipalityCodes.length > 0) {
      const { data: geometryData } = await apolloClient.query<{
        advancedfilteredMunicipalities: {
          total: number;
        };
      }>({
        query: QUERY_GEOMETRY,
        variables: {
          filters,
          pagination: { page: 1, limit: 1 },
          language: currentLanguage,
        },
        fetchPolicy: 'no-cache',
      });

      return {
        advancedFilterOptions: {
          ...data.advancedFilterOptions,
          total_municipalities: geometryData.advancedfilteredMunicipalities.total,
        },
      };
    }

    return data;
  }, [
    createDynamicFilters,
    dimensionLevelFilters,
    opportunityLevels,
    riskLevels,
    intermediateGeographicRegion,
    immediateGeographicRegion,
    apolloClient,
    currentLanguage,
    getFiltersFromUrl,
  ]);

  useEffect(() => {
    // Verificar se estamos na página correta
    if (pathname !== AppRouters.home && !pathname.includes(AppRouters.municipality)) {
      return;
    }

    // Carregamento inicial dos filtros
    if (!hasInitialFiltersLoaded.current) {
      hasInitialFiltersLoaded.current = true;
      setLoadingFilter(true);
      getFilters()
        .then((response) => {
          setFiltersData(response?.advancedFilterOptions.filters);
          setGeometryTotal(response?.advancedFilterOptions.total_municipalities);
        })
        .finally(() => setLoadingFilter(false));
      return;
    }

    const hasFiltersChanged =
      prevFilters.current.region !== region ||
      prevFilters.current.biome !== biome ||
      prevFilters.current.state !== state ||
      prevFilters.current.intermediateGeographicRegion !== intermediateGeographicRegion ||
      prevFilters.current.immediateGeographicRegion !== immediateGeographicRegion ||
      JSON.stringify(prevFilters.current.opportunityLevels) !== JSON.stringify(opportunityLevels) ||
      JSON.stringify(prevFilters.current.riskLevels) !== JSON.stringify(riskLevels) ||
      prevFilters.current.classification !== classification ||
      JSON.stringify(prevFilters.current.dimensionLevelFilters) !==
        JSON.stringify(dimensionLevelFilters);

    prevFilters.current = {
      region,
      biome,
      state,
      intermediateGeographicRegion,
      immediateGeographicRegion,
      opportunityLevels,
      riskLevels,
      classification,
      dimensionLevelFilters,
    };

    if (hasFiltersChanged) {
      setLoadingFilter(true);
      getFilters()
        .then((response) => {
          setFiltersData(response?.advancedFilterOptions.filters);
          setGeometryTotal(response?.advancedFilterOptions.total_municipalities);
        })
        .finally(() => setLoadingFilter(false));
    }
  }, [
    region,
    biome,
    state,
    intermediateGeographicRegion,
    immediateGeographicRegion,
    opportunityLevels,
    riskLevels,
    classification,
    dimensionLevelFilters,
    pathname,
    getFilters,
    setGeometryTotal,
  ]);

  useLayoutEffect(() => {
    if (searchFilter && Object.keys(searchFilter).length > 0) {
      setRegion(
        searchFilter.dynamicFilters?.find((filter) => filter.field === 'region')?.value as string
      );

      setBiome(
        searchFilter.dynamicFilters?.find((filter) => filter.field === 'biome')?.value as string
      );

      setState(
        searchFilter.dynamicFilters?.find((filter) => filter.field === 'state')?.value as string
      );

      setClassification(
        searchFilter.dynamicFilters?.find((filter) => filter.field === 'classification')
          ?.value as Classification
      );

      setIntermediateGeographicRegion(
        firstDynamicFilterCode(searchFilter.dynamicFilters, 'intermediate_region')
      );
      setImmediateGeographicRegion(
        firstDynamicFilterCode(searchFilter.dynamicFilters, 'immediate_region')
      );

      // Buscar opportunityLevels e riskLevels de dimensionLevelFilters com dimension_id: null
      const globalFilter = searchFilter.dimensionLevelFilters?.find(
        (filter) => filter.dimension_id === null
      );

      if (globalFilter) {
        const newOpportunity = (globalFilter.level_opportunity || []) as OpportunityAndRisk[];
        setOpportunityLevels(newOpportunity.length > 0 ? newOpportunity : [OpportunityAndRisk.ALL]);

        const newRisk = (globalFilter.level_risk || []) as OpportunityAndRisk[];
        setRiskLevels(newRisk.length > 0 ? newRisk : [OpportunityAndRisk.ALL]);
      } else {
        setOpportunityLevels([OpportunityAndRisk.ALL]);
        setRiskLevels([OpportunityAndRisk.ALL]);
      }
    }
  }, [searchFilter]);

  return (
    <>
      <StyledSearchContainer>
        <Accordion
          title={t('label.label.territorial_scope')}
          sx={{
            width: '100%',
            borderLeft: 'none',
            borderRight: 'none',
            borderTop: 'none',
            borderRadius: 0,
          }}
          padding={isMobile ? 0 : 2}
          flexDirection="row-reverse"
          expanded={isTerritorialScopeOpen}
          onChange={(_, expanded) => setIsTerritorialScopeOpen(expanded)}
          expandIcon={isTerritorialScopeOpen ? <Remove color="action" /> : <Add color="action" />}
          tag={
            <DimensionTag opportunityCount={globalOpportunityCount} riskCount={globalRiskCount} />
          }
        >
          <Flex direction="column" gap={3} padding={isMobile ? 0 : 1} paddingY={3} fullWidth>
            <Flex direction="column" gap={2} fullWidth>
              <Text variant="subtitle1">
                {t('modal.modal.municipality.advanced_search.risk_and_opportunity_levels')}
              </Text>

              <ScoreFilterContainer>
                <ScoreFilter
                  title={t('label.label.opportunity')}
                  options={opportunityAndRiskOptions}
                  filteredOptions={opportunityLevels}
                  onSelectedOptions={handleOpportunityLevelsChange}
                  variant="opportunity"
                />

                <ScoreFilter
                  title={t('label.label.risk')}
                  options={opportunityAndRiskOptions}
                  filteredOptions={riskLevels}
                  onSelectedOptions={handleRiskLevelsChange}
                  variant="risk"
                />
              </ScoreFilterContainer>
            </Flex>

            <Flex direction="column" gap={2} fullWidth>
              <Text variant="subtitle1">
                {t('modal.modal.municipality.advanced_search.scope_territorial')}
              </Text>

              <Flex direction="column" gap={3} fullWidth>
                <Field>
                  <Label text={t('label.label.region')} htmlFor="region" />
                  <AutocompleteStyled
                    id="region"
                    name="region"
                    placeholder={t('placeholder.placeholder.search.region')}
                    value={regionOptionsMapped.find((o) => o.value === region) || null}
                    onChange={(_, value) => {
                      setRegion(value?.value?.toString() || '');
                    }}
                    margin="none"
                    loading={loadingFilter}
                    options={regionOptionsMapped}
                  />
                </Field>

                <Field>
                  <Label text={t('label.label.biome')} htmlFor="biome" />
                  <AutocompleteStyled
                    id="biome"
                    name="biome"
                    placeholder={t('placeholder.placeholder.search.biome')}
                    value={biomeOptionsMapped.find((o) => o.value === biome) || null}
                    onChange={(_, value) => {
                      setBiome(value?.value?.toString() || '');
                    }}
                    margin="none"
                    loading={loadingFilter}
                    options={biomeOptionsMapped}
                  />
                </Field>
              </Flex>

              <Field>
                <Label text={t('label.label.state')} htmlFor="state" />
                <AutocompleteStyled
                  id="state"
                  name="state"
                  placeholder={t('placeholder.placeholder.search.state')}
                  value={stateOptionsMapped.find((o) => o.value === state) || null}
                  onChange={(_, value) => {
                    setState(value?.value?.toString() || '');
                  }}
                  margin="none"
                  loading={loadingFilter}
                  options={stateOptionsMapped}
                />
              </Field>

              <Field>
                <Label
                  text={t('label.label.intermediate_geographic_region')}
                  htmlFor="intermediate_geographic_region"
                />
                <AutocompleteStyled
                  id="intermediate_geographic_region"
                  name="intermediate_geographic_region"
                  placeholder={t('placeholder.placeholder.search.intermediate_geographic_region')}
                  value={
                    intermediateGeographicRegionOptionsMapped.find(
                      (o) => o.value === intermediateGeographicRegion
                    ) || null
                  }
                  onChange={(_, value) => {
                    const next = value?.value?.toString() || undefined;
                    setIntermediateGeographicRegion(next);
                    setImmediateGeographicRegion(undefined);
                  }}
                  margin="none"
                  loading={loadingFilter}
                  options={intermediateGeographicRegionOptionsMapped}
                />
              </Field>

              <Field>
                <Label
                  text={t('label.label.immediate_geographic_region')}
                  htmlFor="immediate_geographic_region"
                />
                <AutocompleteStyled
                  id="immediate_geographic_region"
                  name="immediate_geographic_region"
                  placeholder={t('placeholder.placeholder.search.immediate_geographic_region')}
                  value={
                    immediateGeographicRegionOptionsMapped.find(
                      (o) => o.value === immediateGeographicRegion
                    ) || null
                  }
                  onChange={(_, value) => {
                    setImmediateGeographicRegion(value?.value?.toString() || undefined);
                  }}
                  margin="none"
                  loading={loadingFilter}
                  options={immediateGeographicRegionOptionsMapped}
                />
              </Field>

              <Field>
                <Label
                  text={t('modal.modal.municipality.advanced_search.rural_urban_classification')}
                  htmlFor="classification"
                />
                <AutocompleteStyled
                  id="classification"
                  name="classification"
                  placeholder={t('placeholder.placeholder.search.classification')}
                  value={
                    classificationOptionsMapped.find((o) => o.value === classification) || null
                  }
                  onChange={(_, value) => {
                    setClassification(value?.value as Classification);
                  }}
                  margin="none"
                  loading={loadingFilter}
                  options={classificationOptionsMapped}
                />
              </Field>
            </Flex>
          </Flex>
        </Accordion>

        <SearchMunicipalityFormDimensions
          initialFilters={dimensionLevelFilters}
          onFiltersChange={(filters) => {
            setDimensionLevelFilters(filters);
          }}
        />
      </StyledSearchContainer>

      <SearchMunicipalityFormFooter>
        <Desktop>
          <Divider variant="fullWidth" />
        </Desktop>

        <Buttons>
          <Button variant="outlined" onClick={clearFilters}>
            <SafeMarkdown text={t('label.label.clear_filters')} />
          </Button>

          <LoadingButton
            loading={loadingGeometry || loadingFilter}
            disabled={geometryTotal === 0 || loadingGeometry || loadingFilter}
            onClick={handleAdvancedFormSubmit}
          >
            {searchResults}
          </LoadingButton>
        </Buttons>
      </SearchMunicipalityFormFooter>
    </>
  );
};
